'use client'

import type { LinkProps } from 'next/link'

import {
  Button,
  ButtonProps,
} from '@snowball-tech/fractal/dist/src/components/Button'
import { cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilArrowLeft as BackIcon } from '@tooni/iconscout-unicons-react'

import type { MouseEvent } from 'react'

import isString from 'lodash/fp/isString'

import { useRouter } from '../helpers/translations'
import useTranslations from '../hooks/useTranslations'
import Link from './Link'

type Props = {
  buttonClassName?: string
  hardLink?: boolean
  href?: string
  type?: 'back' | ButtonProps['type']
} & Omit<ButtonProps, 'href' | 'type'> &
  Omit<LinkProps, 'href' | 'legacyBehavior' | 'onClick' | 'passHref' | 'type'>
export default function RouterButton({
  as,
  buttonClassName,
  children,
  className,
  hardLink = false,
  href,
  icon,
  iconPosition,
  label,
  prefetch,
  replace,
  scroll,
  target,
  type = 'button',
  underlined,
  variant,
  ...buttonProps
}: Props) {
  const router = useRouter()
  const $t = useTranslations()

  const handleClick = (event: MouseEvent<HTMLElement>, back = false) => {
    event.preventDefault()

    buttonProps.onClick?.(event)

    if (back) {
      router.back()
    }
  }

  if (
    type === 'back' &&
    target === '_blank' &&
    isString(href) &&
    isString(as)
  ) {
    return (
      <Button
        className={cn(className, buttonClassName)}
        href={href || as || '#'}
        icon={icon ?? (type === 'back' ? <BackIcon /> : undefined)}
        iconPosition={iconPosition ?? (type === 'back' ? 'left' : undefined)}
        label={label ?? (type === 'back' ? $t('general.back') : undefined)}
        target={target}
        type={type === 'back' ? 'button' : type}
        underlined={underlined ?? (type === 'back' ? false : undefined)}
        variant={variant ?? (type === 'back' ? 'text' : undefined)}
        onClick={(event) => handleClick(event, true)}
        {...buttonProps}
      >
        {children}
      </Button>
    )
  }

  const button = (
    <Button
      className={buttonClassName}
      element={hardLink ? undefined : 'div'}
      href={hardLink ? href || as || '#' : undefined}
      icon={icon ?? (type === 'back' ? <BackIcon /> : undefined)}
      iconPosition={iconPosition ?? (type === 'back' ? 'left' : undefined)}
      label={label ?? (type === 'back' ? $t('general.back') : undefined)}
      target={hardLink ? target : undefined}
      type={type === 'back' ? 'button' : type}
      underlined={underlined ?? (type === 'back' ? false : undefined)}
      variant={variant ?? (type === 'back' ? 'text' : undefined)}
      {...buttonProps}
    >
      {children}
    </Button>
  )

  return hardLink ? (
    button
  ) : (
    <Link
      as={as}
      className={cn(
        variant === 'text' ? '' : 'no-underline',
        underlined === false ? 'no-underline' : '',
        className,
      )}
      href={href || as || '#'}
      prefetch={prefetch}
      replace={replace}
      scroll={scroll}
      target={target}
      onClick={buttonProps.onClick}
    >
      {button}
    </Link>
  )
}
